import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import { GeneratedCharacter, getLatestDramaProject, saveLatestDramaProject } from '../services/dramaProjectStore';

const emptyCharacter: GeneratedCharacter = {
  id: '',
  name: '',
  age: '',
  occupation: '',
  personality: '',
  background: '',
  appearance: '',
  relationships: [],
};

const CharacterSettingPage: React.FC = () => {
  const [characters, setCharacters] = useState<GeneratedCharacter[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<GeneratedCharacter | null>(null);
  const [formCharacter, setFormCharacter] = useState<GeneratedCharacter>(emptyCharacter);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    const latestProject = getLatestDramaProject();
    if (latestProject?.characters?.length) {
      setCharacters(latestProject.characters);
    }
  }, []);

  const relationshipRows = useMemo(() => {
    return characters.flatMap((character) =>
      character.relationships.map((relationship) => ({
        source: character.name,
        relation: relationship,
      })),
    );
  }, [characters]);

  const syncProjectCharacters = (nextCharacters: GeneratedCharacter[]) => {
    const latestProject = getLatestDramaProject();
    if (!latestProject) {
      return;
    }

    saveLatestDramaProject({
      ...latestProject,
      characters: nextCharacters,
    });
  };

  const resetForm = () => {
    setEditingCharacter(null);
    setFormCharacter(emptyCharacter);
    setShowAddForm(false);
  };

  const handleSubmit = () => {
    if (!formCharacter.name.trim() || !formCharacter.personality.trim()) {
      setStatusMessage('至少补齐角色名和性格，再保存。');
      return;
    }

    if (editingCharacter) {
      const nextCharacters = characters.map((character) =>
        character.id === editingCharacter.id ? formCharacter : character,
      );
      setCharacters(nextCharacters);
      syncProjectCharacters(nextCharacters);
      setStatusMessage(`已更新角色：${formCharacter.name}`);
    } else {
      const nextCharacter = {
        ...formCharacter,
        id: formCharacter.id || `${Date.now()}`,
      };
      const nextCharacters = [...characters, nextCharacter];
      setCharacters(nextCharacters);
      syncProjectCharacters(nextCharacters);
      setStatusMessage(`已新增角色：${nextCharacter.name}`);
    }

    resetForm();
  };

  const handleEdit = (character: GeneratedCharacter) => {
    setEditingCharacter(character);
    setFormCharacter(character);
    setShowAddForm(true);
  };

  const handleDelete = (characterId: string) => {
    if (!confirm('确定要删除这个角色吗？')) {
      return;
    }

    const nextCharacters = characters.filter((character) => character.id !== characterId);
    setCharacters(nextCharacters);
    syncProjectCharacters(nextCharacters);
    setStatusMessage('角色已删除。');
  };

  const handleLoadFromLatestScript = () => {
    const latestProject = getLatestDramaProject();
    if (!latestProject?.characters?.length) {
      setStatusMessage('还没有可同步的剧本角色，请先去创作中心生成剧本。');
      return;
    }

    setCharacters(latestProject.characters);
    setStatusMessage(`已从《${latestProject.title}》同步 ${latestProject.characters.length} 个角色。`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-purple-600">首页</Link>
          <span>/</span>
          <span className="text-gray-900">角色设定</span>
        </nav>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">角色设定管理</h1>
          <p className="text-gray-600">把刚生成的剧本角色整理成可继续使用的角色档案。</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
          >
            ➕ 手动添加角色
          </button>
          <button
            onClick={handleLoadFromLatestScript}
            className="px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
          >
            🎭 从当前剧本生成角色
          </button>
          <Link
            to="/create"
            className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors text-center"
          >
            📝 返回剧本创作
          </Link>
        </div>

        {statusMessage && (
          <div className="mb-6 rounded-lg bg-purple-50 px-4 py-3 text-sm text-purple-700">
            {statusMessage}
          </div>
        )}

        {showAddForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {editingCharacter ? '编辑角色' : '添加新角色'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">角色姓名</label>
                <input
                  type="text"
                  value={formCharacter.name}
                  onChange={(e) => setFormCharacter({ ...formCharacter, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入角色姓名"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">年龄</label>
                <input
                  type="text"
                  value={formCharacter.age}
                  onChange={(e) => setFormCharacter({ ...formCharacter, age: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入年龄"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">职业</label>
                <input
                  type="text"
                  value={formCharacter.occupation}
                  onChange={(e) => setFormCharacter({ ...formCharacter, occupation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入职业"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">性格特征</label>
                <input
                  type="text"
                  value={formCharacter.personality}
                  onChange={(e) => setFormCharacter({ ...formCharacter, personality: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入性格特征"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">背景故事</label>
                <textarea
                  value={formCharacter.background}
                  onChange={(e) => setFormCharacter({ ...formCharacter, background: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-24 resize-none"
                  placeholder="请输入背景故事"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">外貌描述</label>
                <textarea
                  value={formCharacter.appearance}
                  onChange={(e) => setFormCharacter({ ...formCharacter, appearance: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-20 resize-none"
                  placeholder="请输入外貌描述"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">人物关系</label>
                <input
                  type="text"
                  value={formCharacter.relationships.join('、')}
                  onChange={(e) =>
                    setFormCharacter({
                      ...formCharacter,
                      relationships: e.target.value
                        .split(/[、,，]/)
                        .map((item) => item.trim())
                        .filter(Boolean),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="用顿号或逗号分隔多个关系"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={handleSubmit}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                {editingCharacter ? '确认更新' : '确认添加'}
              </button>
              <button
                onClick={resetForm}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                取消
              </button>
            </div>
          </div>
        )}

        {characters.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {characters.map((character) => (
              <div key={character.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4">
                    {character.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{character.name}</h3>
                    <p className="text-sm text-gray-600">{character.age} · {character.occupation}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">性格特征</h4>
                    <p className="text-sm text-gray-600">{character.personality}</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">背景故事</h4>
                    <p className="text-sm text-gray-600">{character.background}</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">外貌描述</h4>
                    <p className="text-sm text-gray-600">{character.appearance}</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">人物关系</h4>
                    <div className="flex flex-wrap gap-2">
                      {character.relationships.map((relationship) => (
                        <span key={relationship} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          {relationship}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(character)}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    编辑
                  </button>
                  <button
                    onClick={() => handleDelete(character.id)}
                    className="flex-1 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                  >
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="text-5xl mb-4">🎭</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">还没有角色档案</h3>
            <p className="text-gray-600 mb-6">先去创作中心生成剧本，再回这里一键同步角色。</p>
            <Link
              to="/create"
              className="inline-flex px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              去生成剧本
            </Link>
          </div>
        )}

        <div className="mt-12 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">人物关系图谱</h3>
          {relationshipRows.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {relationshipRows.map((row, index) => (
                <div key={`${row.source}-${row.relation}-${index}`} className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                  <div className="text-sm text-gray-500 mb-1">关系起点</div>
                  <div className="font-semibold text-gray-900">{row.source}</div>
                  <div className="text-sm text-purple-600 mt-2">{row.relation}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600">同步到角色以后，这里会自动展示人物关系。</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterSettingPage;
